/**
 * V2OptionsListSection — V2-native editor section for step.content.options
 *
 * Unified section for all list types (ListSinglePick, ListMultiPick, Grid*, HeroList).
 * Edits: selectionMode, columnLayout (grid types only), itemLayout, items array.
 * The list animations (item entrance + stagger, selection/deselection feedback) are the
 * last style section, "Animations" (V2ListAnimationFields), behind the gear.
 * V2 paths: content.options.*
 *
 * Uses V2ListItemEditor (cloned from V1 ListItemEditor) for card-style per-item editing.
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Collapse,
  InputLabel,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { SAccordion, SortableList, SDialog } from '@modules/ui-components';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import { EditorGuideProvider } from '../../../contexts';
import { useVariableEditor } from '../../../contexts/VariableEditorContext';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import {
  V2_SELECTION_MODE_OPTIONS,
  V2_COLUMN_LAYOUT_OPTIONS,
  buildItemLayoutOptions,
  getV2OptionsItemVisibility,
  createV2OptionsItemFromSections,
  getValidItemLayoutsForContext,
} from '../../../config/v2OptionsListConfig';
import { createV2OptionsListStyleSections } from '../../../config/v2OptionsListStyleConfig';
import { getV2ScreenTypeConfig, shouldShowColumnLayoutSelector } from '../../../config/v2ScreenTypeConfig';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import { GRID_ITEM_LAYOUT_PRESETS, applyGridPreset } from '../../../config/gridItemLayoutPresets';
import {
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath } from '../../../utils/v2FieldOperations';
import { getV2TranslationValue } from '../../../utils/v2FieldOperations';
import { getListActionTestId, getSectionTestId } from '../../../utils/testIdUtils';
import { V2ListStyleSections } from './list/V2ListStyleSections';
import { V2ListAnimationFields } from './list/V2ListAnimationFields';
import { V2ListItemEditor } from './list/V2ListItemEditor';
import { OPTIONS_DISCLOSURE_THRESHOLD } from '@modules/screen-view-v2/components/shared/OptionsList';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';

interface EditableItem {
  id: string;
  originalIndex: number;
  title: string;
  subtitle: string;
  imageUrl: string | null;
  variableValue: string | null;
}

export interface V2OptionsListSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  /** When set, hides the selectionMode dropdown and forces this mode */
  lockedSelectionMode?: 'browse' | 'one' | 'many';
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
  /** Render function for embedded subsections (e.g. Variable Binding) */
  renderSubsections?: () => React.ReactNode;
  /** Badge count from subsections to aggregate with own badge */
  subsectionBadgeCount?: number;
  /** Where to render subsections relative to own content */
  subsectionPosition?: 'first' | 'last';

  /** Theme settings for themed defaults on list item creation */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

export const V2OptionsListSection: React.FC<V2OptionsListSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  lockedSelectionMode,
  sectionTitle,
  renderSubsections,
  subsectionBadgeCount,
  subsectionPosition,
  themeSettingsV2: _themeSettingsV2,
}) => {
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);
  const lastUploadedRef = useRef<{ original: string; small: string } | null>(null);

  const { variables, variablesVersion } = useVariableEditor();

  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    itemIndex: number;
    itemTitle: string;
  }>({ open: false, itemIndex: -1, itemTitle: '' });

  // Style sections config (memoized, stable across renders); "Animations" is the last one
  const styleSections = useMemo(
    () => createV2OptionsListStyleSections({ animationFields: V2ListAnimationFields }),
    [],
  );
  const [stylesExpanded, setStylesExpanded] = useState(false);
  const handleStylesToggle = useCallback(() => setStylesExpanded(prev => !prev), []);

  // Screen type config — used for layout options and hiding irrelevant fields
  const screenTypeConfig = useMemo(() => getV2ScreenTypeConfig(step.screenType), [step.screenType]);

  // HeroList (itemsHaveActions: false) is display-only — hide selection/column dropdowns
  const showSelectionAndLayout = screenTypeConfig.itemsHaveActions !== false;

  // When lockedSelectionMode is set, hide the selectionMode dropdown
  const showSelectionMode = !lockedSelectionMode && showSelectionAndLayout;

  // Column Layout — the screen type decides the column count on lists (and on
  // FlexibleLayout list sections, which proxy ListSinglePick); grids keep the selector.
  const showColumnLayout = shouldShowColumnLayoutSelector(screenTypeConfig);

  // Read options config
  const selectionMode = useMemo(
    () => getValueByPath<string>(step, 'content.options.selectionMode') ?? 'one',
    [step]
  );

  const columnLayout = useMemo(
    () => getValueByPath<string>(step, 'content.options.columnLayout') ?? 'oneCol',
    [step]
  );

  const itemLayout = useMemo(
    () => getValueByPath<string>(step, 'content.options.itemLayout') ?? 'Title',
    [step]
  );

  // Effective selectionMode (lockedSelectionMode overrides stored value)
  const effectiveSelectionMode = lockedSelectionMode ?? selectionMode;

  // Build ItemLayout dropdown options filtered by columnLayout + selectionMode.
  // getValidItemLayoutsForContext resolves (columnLayout, selectionMode) → matching
  // screen type config (e.g. twoCols+one → GridSinglePick) and returns its validItemLayouts.
  // No intersection with screenTypeConfig needed — the context function IS the source of truth.
  const itemLayoutOptions = useMemo(() => {
    const validLayouts = getValidItemLayoutsForContext(columnLayout, effectiveSelectionMode);
    return buildItemLayoutOptions(validLayouts);
  }, [columnLayout, effectiveSelectionMode]);

  const rawItems = useMemo(() => {
    const items = getValueByPath<unknown[]>(step, 'content.options.items');
    return Array.isArray(items) ? items : [];
  }, [step]);

  const visibility = useMemo(() => getV2OptionsItemVisibility(itemLayout), [itemLayout]);

  // Transform to editable items
  const editableItems: EditableItem[] = useMemo(() => {
    return rawItems.map((_, index) => {
      const title = getV2TranslationValue(step, `content.options.items.${index}.title`, locale);
      const subtitle = getV2TranslationValue(step, `content.options.items.${index}.subtitle`, locale);
      const imageAsset = getValueByPath<Record<string, string>>(
        step,
        `content.options.items.${index}.image.translations.${locale}`
      );
      const imageUrl = imageAsset
        ? (typeof imageAsset === 'string' ? imageAsset : imageAsset.original ?? imageAsset.origin ?? null)
        : null;

      const variableValue = getValueByPath<string>(step, `content.options.items.${index}.variableValue`) ?? null;

      return {
        id: `option-${index}`,
        originalIndex: index,
        title,
        subtitle,
        imageUrl,
        variableValue,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rawItems, locale, variablesVersion]);

  // Find the variable bound to this screen via boundScreenIds (variable is single source of truth)
  const currentScreenId = step.entityId || (step as any).id || '';
  const boundVariable = useMemo(() => {
    return Object.values(variables).find(v => {
      const boundScreenIds = (v as unknown as Record<string, string[]>).boundScreenIds;
      return boundScreenIds?.includes(currentScreenId);
    }) ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, currentScreenId, variablesVersion]);

  // Bound variable's options — passed to V2ListItemEditor for dropdown
  const boundVariableOptions = useMemo(() => {
    return boundVariable?.options ?? [];
  }, [boundVariable]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSelectionModeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newMode = event.target.value;
      onChange('content.options.selectionMode', newMode);
      // Auto-reset itemLayout if current value is invalid for the new selectionMode
      const validLayouts = getValidItemLayoutsForContext(columnLayout, newMode);
      if (!validLayouts.includes(itemLayout)) {
        onChange('content.options.itemLayout', validLayouts[0]);
      }
    },
    [onChange, columnLayout, itemLayout]
  );

  const handleColumnLayoutChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newColumnLayout = event.target.value;
      onChange('content.options.columnLayout', newColumnLayout);
      // Auto-reset itemLayout if current value is invalid for the new columnLayout
      const validLayouts = getValidItemLayoutsForContext(newColumnLayout, effectiveSelectionMode);
      if (!validLayouts.includes(itemLayout)) {
        onChange('content.options.itemLayout', validLayouts[0]);
      }
    },
    [onChange, effectiveSelectionMode, itemLayout]
  );

  // Auto-populate container settings for existing screens that have a grid
  // itemLayout but no saved imageContainer (backward compat one-time migration)
  useEffect(() => {
    if (GRID_ITEM_LAYOUT_PRESETS[itemLayout]) {
      const imgContainer = getValueByPath(step, 'content.options.appearance.imageContainer');
      if (!imgContainer) {
        const currentOptions = getValueByPath<Record<string, unknown>>(step, 'content.options') ?? {};
        applyGridPreset(onChange, itemLayout, currentOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const handleItemLayoutChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newLayout = event.target.value;
      if (GRID_ITEM_LAYOUT_PRESETS[newLayout]) {
        const currentOptions = getValueByPath<Record<string, unknown>>(step, 'content.options') ?? {};
        applyGridPreset(onChange, newLayout, currentOptions);
      } else {
        onChange('content.options.itemLayout', newLayout);
      }
    },
    [onChange, step]
  );

  const handleItemTitleChange = useCallback(
    (index: number, value: string) => {
      onChange(`content.options.items.${index}.title.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const handleItemSubtitleChange = useCallback(
    (index: number, value: string) => {
      onChange(`content.options.items.${index}.subtitle.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const handleItemImageChange = useCallback(
    (index: number, value: string | null) => {
      if (value) {
        const asset = lastUploadedRef.current ?? { original: value, small: value };
        onChange(`content.options.items.${index}.image.translations.${locale}`, asset);
        lastUploadedRef.current = null;
      } else {
        onChange(`content.options.items.${index}.image.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleVariableValueChange = useCallback(
    (index: number, value: string): string | null => {
      // Duplicate check — reject if another item already uses this variableValue
      const isDuplicate = editableItems.some(
        item => item.originalIndex !== index && item.variableValue === value
      );
      if (isDuplicate) {
        return `"${value}" is already used by another item`;
      }

      onChange(`content.options.items.${index}.variableValue`, value);
      return null; // success — variable option management is explicit in Variable Binding section
    },
    [onChange, editableItems]
  );

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadService.uploadImage(file, {
        projectId: projectId!,
        onboardingId: onboardingId!,
        versionId: versionId!,
        locale,
      });
      lastUploadedRef.current = { original: result.assetUrl.origin, small: result.assetUrl.small };
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale]
  );

  const handleAddItem = useCallback(() => {
    const newIndex = rawItems.length;

    // Collect existing variableValues to avoid collisions
    const usedValues = new Set(
      rawItems.map((_, i) =>
        getValueByPath<string>(step, `content.options.items.${i}.variableValue`)
      ).filter(Boolean)
    );

    // Generate next available item_N id
    const nextItemId = (): string => {
      let n = newIndex;
      while (usedValues.has(`item_${n}`)) n++;
      return `item_${n}`;
    };

    // Always assign item_N — variable option management is explicit in Variable Binding section
    const autoVariableValue = nextItemId();

    // Create item with appearance from style section configs (reads items.0 values, falls back to defaults)
    const newItem = createV2OptionsItemFromSections(step, locale, newIndex + 1, autoVariableValue);
    const newItems = [...rawItems, newItem];
    onChange('content.options.items', newItems);
  }, [rawItems, locale, onChange, step]);

  const handleDeleteRequest = useCallback(
    (index: number) => {
      const title = editableItems.find((i) => i.originalIndex === index)?.title ?? '';
      setDeleteDialogState({ open: true, itemIndex: index, itemTitle: title });
    },
    [editableItems]
  );

  const handleDeleteConfirm = useCallback(() => {
    const newItems = rawItems.filter((_, idx) => idx !== deleteDialogState.itemIndex);
    onChange('content.options.items', newItems);
    setDeleteDialogState({ open: false, itemIndex: -1, itemTitle: '' });
  }, [deleteDialogState, rawItems, onChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogState({ open: false, itemIndex: -1, itemTitle: '' });
  }, []);

  const handleReorder = useCallback(
    (reorderedItems: EditableItem[]) => {
      const newRawItems = reorderedItems.map((item) => rawItems[item.originalIndex]);
      onChange('content.options.items', newRawItems);
    },
    [rawItems, onChange]
  );

  const SECTION_NAME = 'Options';

  return (
    <>
      <SAccordion
        title={sectionTitle ?? 'Options List'}
        collapsible
        defaultExpanded={defaultExpanded}
        badge={subsectionBadgeCount || undefined}
        badgeColor={subsectionBadgeCount ? 'warning' : undefined}
        sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
        data-testid={getSectionTestId(SECTION_NAME)}
      >
        {subsectionPosition === 'first' && renderSubsections?.()}
        <EditorGuideProvider>
          {/* Selection Mode — hidden for display-only lists (HeroList) and locked modes */}
          {showSelectionMode && (
            <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Selection Mode</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectionMode}
                  onChange={handleSelectionModeChange}
                  disabled={disabled}
                  data-testid="select-options-selection-mode"
                >
                  {V2_SELECTION_MODE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Column Layout — grids only (lists and HeroList are one column by type) */}
          {showColumnLayout && (
            <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Column Layout</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={columnLayout}
                  onChange={handleColumnLayoutChange}
                  disabled={disabled}
                  data-testid="select-options-column-layout"
                >
                  {V2_COLUMN_LAYOUT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Item Layout + Gear icon for style settings */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: FIELD_MARGIN_BOTTOM }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Item Layout</InputLabel>
              <Select
                value={itemLayout}
                label="Item Layout"
                onChange={handleItemLayoutChange}
                disabled={disabled}
                data-testid="select-options-item-layout"
              >
                {itemLayoutOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              onClick={handleStylesToggle}
              disabled={disabled}
              size="small"
              data-testid="style-settings-button"
              sx={stylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* "More options" disclosure copy (W8 M5, controller ruling R-W70-37).
              The words the visitor reads are the OWNER's, carried by the compiled
              document — the platform authors nothing at render time, and the runtime
              shows no disclosure without them. Compile seeds a default in the funnel's
              default language; this is where it is edited, and the funnel localization
              export/import carries it like any other LocalizedLabel.
              Shown only past the threshold, because below it the control never appears. */}
          {showSelectionAndLayout && rawItems.length > OPTIONS_DISCLOSURE_THRESHOLD && (
            <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                &quot;More options&quot; disclosure
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                This list is longer than {OPTIONS_DISCLOSURE_THRESHOLD} options, so the rest sit behind
                a control. Leave both empty to show every option at once.
              </Typography>
              <TextField
                size="small"
                fullWidth
                label="Reveal label"
                value={getV2TranslationValue(step, `content.options.moreOptionsLabel`, locale) ?? ''}
                onChange={(e) => onChange(`content.options.moreOptionsLabel.translations.${locale}`, e.target.value)}
                disabled={disabled}
                sx={{ mb: 1 }}
                data-testid="input-options-more-label"
              />
              <TextField
                size="small"
                fullWidth
                label="Collapse label"
                value={getV2TranslationValue(step, `content.options.fewerOptionsLabel`, locale) ?? ''}
                onChange={(e) => onChange(`content.options.fewerOptionsLabel.translations.${locale}`, e.target.value)}
                disabled={disabled}
                data-testid="input-options-fewer-label"
              />
            </Box>
          )}

          {/* Style Sections (collapsible) — includes Selection behavior, List position, etc. */}
          <Collapse in={stylesExpanded} timeout="auto">
            <Box sx={{ pl: 1, mb: 2 }} data-testid="style-sections-container">
              <V2ListStyleSections
                sections={styleSections}
                currentItemType={itemLayout}
                visibilityContext={{ columnLayout, selectionMode: effectiveSelectionMode }}
                step={step}
                onChange={onChange}
                disabled={disabled}
              />
            </Box>
          </Collapse>

          {/* Add Item Button */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              disabled={disabled}
              data-testid={getListActionTestId('Options', 'add')}
              sx={{ textTransform: 'none', minWidth: 'auto' }}
            >
              Add Item
            </Button>
          </Box>

          {/* Items List */}
          {editableItems.length > 0 ? (
            <SortableList
              items={editableItems}
              onReorder={handleReorder}
              gap="sm"
              disabled={disabled}
            >
              {(item: EditableItem) => (
                <SortableList.Item showDragHandle dragHandlePosition="start" disabled={disabled}>
                  <V2ListItemEditor
                    index={item.originalIndex}
                    title={item.title}
                    subtitle={item.subtitle}
                    imageUrl={item.imageUrl}
                    variableValue={item.variableValue}
                    variableOptions={boundVariableOptions.length > 0 ? boundVariableOptions : undefined}
                    showSubtitle={visibility.showSubtitle}
                    showImage={visibility.showImage}
                    showVariableValue={lockedSelectionMode !== 'browse'}
                    onTitleChange={(value) => handleItemTitleChange(item.originalIndex, value)}
                    onSubtitleChange={
                      visibility.showSubtitle
                        ? (value) => handleItemSubtitleChange(item.originalIndex, value)
                        : undefined
                    }
                    onImageChange={
                      visibility.showImage
                        ? (value) => handleItemImageChange(item.originalIndex, value)
                        : undefined
                    }
                    onImageUpload={visibility.showImage ? handleImageUpload : undefined}
                    onVariableValueChange={(value) => handleVariableValueChange(item.originalIndex, value)}
                    onDelete={() => handleDeleteRequest(item.originalIndex)}
                    disabled={disabled}
                  />
                </SortableList.Item>
              )}
            </SortableList>
          ) : (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              No items
            </Typography>
          )}
        </EditorGuideProvider>
        {subsectionPosition === 'last' && renderSubsections?.()}
      </SAccordion>

      {/* Delete Confirmation Dialog */}
      <SDialog
        open={deleteDialogState.open}
        onClose={handleDeleteCancel}
        title={`Delete Item ${deleteDialogState.itemIndex + 1}?`}
        maxWidth="xs"
        fullWidth
        // Destructive confirmation.
        showCloseButton={false}
        primaryAction={{
          label: 'Delete',
          onClick: handleDeleteConfirm,
          color: 'error',
          variant: 'contained',
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: handleDeleteCancel,
          variant: 'outlined',
        }}
      >
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{deleteDialogState.itemTitle || `Item ${deleteDialogState.itemIndex + 1}`}&quot;?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </Box>
      </SDialog>
    </>
  );
};
